import React, { forwardRef, useRef, useState } from 'react';
import { Col, Label } from 'reactstrap';
import axios from 'axios';
import { api } from '../../../config';
import notify from '../../../utils/notify';
import { buttonProps } from '../../../utils/a11y';
import ActionButton from '../../../Components/Common/ActionButton';
import { isSafeExternalUrl } from '../utils/hpsHelpers';

// Sama dengan validasi BE (ValidateNotaDinasDocument).
const ALLOWED_EXT = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'];
const MAX_SIZE = 10 * 1024 * 1024;

// Blob disimpan sebagai `${guid}_${namaFile}`; tampilkan nama aslinya.
const displayFileName = path =>
  path.includes('_') ? path.substring(path.indexOf('_') + 1) : path;

const validateFile = file => {
  const ext = file.name.includes('.')
    ? file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
    : '';
  if (!ALLOWED_EXT.includes(ext))
    return `File type is not allowed. Allowed: ${ALLOWED_EXT.join(', ')}`;
  if (file.size === 0) return 'File cannot be empty.';
  if (file.size > MAX_SIZE)
    return 'File size exceeds the maximum allowed limit of 10MB.';
  return null;
};

/**
 * Document Nota Dinas Rilis di modul HPS. Upload/Replace hanya selama HPS
 * Not Available; setelah Available tinggal View. File langsung diupload saat
 * dipilih (PUT /hps/upload-nota-dinas-rilis/{id}).
 */
const NotaDinasRilisUpload = forwardRef(
  ({ procurementId, token, path, url, editable, error, onUploaded }, ref) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const pickFile = () => {
      if (!uploading) inputRef.current?.click();
    };

    const handleFileChange = async e => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;

      const invalid = validateFile(file);
      if (invalid) {
        notify.error(invalid);
        return;
      }

      const formData = new FormData();
      formData.append('DocumentNotaDinasRilis', file);
      setUploading(true);
      try {
        await axios.put(
          `${api.API_URL}/hps/upload-nota-dinas-rilis/${procurementId}`,
          formData,
          {
            withXSRFToken: true,
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        notify.success('Nota Dinas Rilis uploaded successfully.');
        await onUploaded?.();
      } catch (err) {
        notify.apiError(err, 'upload Nota Dinas Rilis');
      } finally {
        setUploading(false);
      }
    };

    return (
      <Col md={12} lg={12}>
        <div className='mb-3' ref={ref}>
          <Label className='fw-semibold small text-dark mb-2'>
            Document Nota Dinas Rilis
          </Label>

          {editable && (
            <input
              type='file'
              accept={ALLOWED_EXT.join(',')}
              ref={inputRef}
              onChange={handleFileChange}
              disabled={uploading}
              style={{ display: 'none' }}
              aria-label='Document Nota Dinas Rilis file'
            />
          )}

          {path ? (
            <div className='border rounded-3 p-2 d-flex align-items-center justify-content-between bg-light'>
              <div className='d-flex align-items-center overflow-hidden me-2'>
                <div
                  className='d-flex align-items-center justify-content-center rounded bg-primary-subtle p-2 me-2 text-primary'
                  style={{ width: '36px', height: '36px', flexShrink: 0 }}
                >
                  <i className='ri-file-text-line fs-20'></i>
                </div>
                <span className='small text-dark fw-medium text-break'>
                  {displayFileName(path)}
                </span>
              </div>
              <div className='d-flex align-items-center gap-1 flex-shrink-0'>
                {url && (
                  <ActionButton
                    variant='secondary'
                    size='sm'
                    icon='ri-eye-line'
                    onClick={() =>
                      isSafeExternalUrl(url) &&
                      window.open(url, '_blank', 'noopener,noreferrer')
                    }
                  >
                    View
                  </ActionButton>
                )}
                {editable && (
                  <ActionButton
                    variant='secondary'
                    size='sm'
                    icon='ri-upload-2-line'
                    loading={uploading}
                    onClick={pickFile}
                  >
                    Replace
                  </ActionButton>
                )}
              </div>
            </div>
          ) : editable ? (
            <div
              className={`border rounded-3 p-3 d-flex align-items-center bg-white ${
                error ? 'border-danger' : ''
              }`}
              style={{
                cursor: uploading ? 'not-allowed' : 'pointer',
                opacity: uploading ? 0.6 : 1,
              }}
              {...buttonProps(pickFile, 'Upload Document Nota Dinas Rilis', {
                'aria-disabled': uploading,
              })}
            >
              <div
                className='d-flex align-items-center justify-content-center rounded-circle me-3'
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: error ? '#fdf2f2' : '#f3f6fa',
                  flexShrink: 0,
                }}
              >
                <i
                  className={`${uploading ? 'ri-loader-4-line' : 'ri-upload-2-line'} fs-20 ${error ? 'text-danger' : 'text-secondary'}`}
                ></i>
              </div>
              <div>
                <h5 className='fs-14 fw-semibold text-dark mb-0'>
                  {uploading ? 'Uploading...' : 'Upload File'}
                </h5>
                <span className='text-muted small fs-12'>
                  Browse from your device (PDF, Word, Excel, max 10MB)
                </span>
              </div>
            </div>
          ) : (
            <p className='mb-0 text-muted small'>-</p>
          )}

          {error && (
            <div className='text-danger small mt-1' role='alert'>
              {error}
            </div>
          )}
        </div>
      </Col>
    );
  }
);

NotaDinasRilisUpload.displayName = 'NotaDinasRilisUpload';

export default NotaDinasRilisUpload;
